import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory";
import UseFetchPurchaseItems from "../../helper/UseFetchPurchaseItems";
import useDebounce from "../../hooks/UseDebounce";
import {
  getInvoiceItemsByBatch,
  getMedicineStockReport,
} from "../../connections/getApi";

const isoDate = (d) => d.toISOString().slice(0, 10);

// normalise any date to YYYY-MM-DD so two sources can be compared safely
const normDate = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  const dmy = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  return s;
};

// "2027-04-14" -> "14-04-2027" for display
const showDate = (s) => {
  const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : s || "—";
};

const headCell = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#3A3A3A",
  whiteSpace: "nowrap",
  padding: "8px 10px",
};
const bodyCell = {
  fontSize: "12px",
  color: "#3A3A3A",
  whiteSpace: "nowrap",
  padding: "7px 10px",
};

const fmtNum = (v) =>
  v != null && v !== ""
    ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

// quantities come back as floats (1.0, 6.0) — drop the ".00" when it's whole
const fmtQty = (v) => {
  const n = Number(v);
  if (v == null || v === "" || Number.isNaN(n)) return "—";
  return n % 1 === 0 ? String(n) : n.toFixed(2);
};

const sumBy = (arr, key) =>
  arr.reduce((s, it) => s + (Number(it[key]) || 0), 0);

const StatCard = ({ label, value, color = "#007A99", sub }) => (
  <Box
    sx={{
      flex: "1 1 140px",
      minWidth: "130px",
      background: `linear-gradient(135deg, ${color}18 0%, ${color}28 100%)`,
      border: `1.5px solid ${color}44`,
      borderRadius: "10px",
      px: "14px",
      py: "12px",
    }}
  >
    <Typography sx={{ fontSize: "10.5px", color: "#777", mb: "4px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "20px", fontWeight: 700, color, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: "10.5px", color: "#888", mt: "3px" }}>
        {sub}
      </Typography>
    )}
  </Box>
);

// ─── Sales & Refunds panel (second tab of the stock dialog) ─────────────────
// Splits /pharmacy/invoice_items on the `refund` flag: refund=false rows are
// sales, refund=true rows are returns (their amount/cost come back negative).
const SalesRefundPanel = ({
  loading,
  error,
  items,
  currentStockLeft,
  batchLabel,
}) => {
  const [lineFilter, setLineFilter] = useState("all"); // all | sale | refund
  const [linePage, setLinePage] = useState(1);
  const [linesPerPage, setLinesPerPage] = useState(25);

  // a new medicine/batch (or a different filter) always starts at page 1
  useEffect(() => {
    setLinePage(1);
  }, [items, lineFilter, linesPerPage]);

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: "40px" }}>
        <CircularProgress size={22} sx={{ color: "#007A99" }} />
        <Typography sx={{ fontSize: "12.5px", color: "#999", mt: "10px" }}>
          Loading sales &amp; refunds…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: "30px", color: "#D32F2F", fontSize: "13px" }}>
        {error}
      </Box>
    );
  }

  const sold = items.filter((it) => !it.refund);
  const refunded = items.filter((it) => it.refund);

  // totals always run over the full result set, never the visible page
  const soldQty = sumBy(sold, "quantity");
  const refundQty = sumBy(refunded, "quantity");
  const netQty = soldQty - refundQty;

  const saleValue = sumBy(sold, "amount");
  const refundValue = Math.abs(sumBy(refunded, "amount"));
  const netRevenue = sumBy(items, "amount");
  const soldDiscount = sumBy(sold, "discount");

  const scope = batchLabel ? `Batch: ${batchLabel}` : "All batches";

  const filteredLines =
    lineFilter === "sale" ? sold : lineFilter === "refund" ? refunded : items;

  const lineTotalPages = Math.max(1, Math.ceil(filteredLines.length / linesPerPage));
  const safePage = Math.min(linePage, lineTotalPages);
  const sliceStart = (safePage - 1) * linesPerPage;
  const visibleLines = filteredLines.slice(sliceStart, sliceStart + linesPerPage);

  return (
    <>
      {items.length === 0 ? (
        <Box sx={{ textAlign: "center", py: "30px", color: "#999", fontSize: "13px" }}>
          No sales or refunds recorded for {batchLabel ? `batch ${batchLabel}` : "this medicine"}.
        </Box>
      ) : (
        <>
          {/* quantity cards */}
          <Box sx={{ display: "flex", gap: "12px", mb: "10px", mt: "16px", flexWrap: "wrap" }}>
            <StatCard
              label="Sold"
              value={fmtQty(soldQty)}
              color="#2E7D32"
              sub={`${sold.length} sale line${sold.length === 1 ? "" : "s"}  ·  ${scope}`}
            />
            <StatCard
              label="Refunded"
              value={fmtQty(refundQty)}
              color="#D32F2F"
              sub={`${refunded.length} refund line${refunded.length === 1 ? "" : "s"}`}
            />
            <StatCard
              label="Net Sold"
              value={fmtQty(netQty)}
              color="#1565C0"
              sub="Sold − refunded"
            />
            <StatCard
              label="Current Stock Left"
              value={currentStockLeft != null ? currentStockLeft : "—"}
              color={currentStockLeft != null && currentStockLeft < 20 ? "#D32F2F" : "#455A64"}
              sub={scope}
            />
          </Box>

          {/* money cards */}
          <Box sx={{ display: "flex", gap: "12px", mb: "20px", flexWrap: "wrap" }}>
            <StatCard
              label="Sale Value"
              value={`₹${fmtNum(saleValue)}`}
              color="#2E7D32"
              sub="Billed amount on sale lines"
            />
            <StatCard
              label="Refund Value"
              value={`₹${fmtNum(refundValue)}`}
              color="#D32F2F"
              sub="Amount returned to patients"
            />
            <StatCard
              label="Net Revenue"
              value={`₹${fmtNum(netRevenue)}`}
              color="#6A1B9A"
              sub="Sale − refund"
            />
            <StatCard
              label="Discount Given"
              value={`₹${fmtNum(soldDiscount)}`}
              color="#E65100"
              sub="On sale lines"
            />
          </Box>

          <Divider sx={{ mb: "16px" }} />

          {/* line-list toolbar: type filter + rows per page */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
              mb: "10px",
            }}
          >
            <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#3A3A3A" }}>
              Invoice Lines
            </Typography>

            <Box sx={{ display: "flex", gap: "6px" }}>
              {[
                { key: "all", label: `All (${items.length})` },
                { key: "sale", label: `Sales (${sold.length})` },
                { key: "refund", label: `Refunds (${refunded.length})` },
              ].map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  size="small"
                  onClick={() => setLineFilter(f.key)}
                  sx={{
                    fontSize: "11px",
                    height: "22px",
                    cursor: "pointer",
                    fontWeight: lineFilter === f.key ? 700 : 500,
                    bgcolor: lineFilter === f.key ? "#007A99" : "#EEF3F5",
                    color: lineFilter === f.key ? "#fff" : "#4A5A60",
                    "&:hover": {
                      bgcolor: lineFilter === f.key ? "#006c88" : "#E0E8EB",
                    },
                  }}
                />
              ))}
            </Box>

            <TextField
              select
              size="small"
              value={linesPerPage}
              onChange={(e) => setLinesPerPage(Number(e.target.value))}
              SelectProps={{ native: true }}
              sx={{
                ml: "auto",
                width: "92px",
                "& select": { fontSize: "11.5px", padding: "4px 8px" },
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              }}
            >
              {[25, 50, 100, 250].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </TextField>
          </Box>

          <TableContainer sx={{ borderRadius: "8px", border: "1px solid #E0E0E0", background: "#fff", maxHeight: "300px" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ background: "#F5F5F5" }}>
                  {["Item ID", "Type", "Batch", "Qty", "Cost / Unit (₹)", "Disc / Unit (₹)", "Discount (₹)", "Amount (₹)"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: "11px", fontWeight: 700, color: "#555", whiteSpace: "nowrap", py: "7px", px: "10px", background: "#F5F5F5" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleLines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ fontSize: "12px", textAlign: "center", py: "22px", color: "#999" }}>
                      No {lineFilter === "refund" ? "refund" : "sale"} lines for this batch.
                    </TableCell>
                  </TableRow>
                )}
                {visibleLines.map((it) => (
                  <TableRow
                    key={it.id}
                    sx={{
                      background: it.refund ? "#FFF4F3" : "transparent",
                      "&:hover": { background: it.refund ? "#FFE9E7" : "#F9F9F9" },
                    }}
                  >
                    <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{it.id}</TableCell>
                    <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>
                      <Chip
                        label={it.refund ? "Refund" : "Sale"}
                        size="small"
                        sx={{
                          fontSize: "10px",
                          height: "18px",
                          bgcolor: it.refund ? "#FFEBEE" : "#E8F5E9",
                          color: it.refund ? "#C62828" : "#2E7D32",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{it.batch || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px", fontWeight: 600 }}>{fmtQty(it.quantity)}</TableCell>
                    <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{fmtNum(it.cost_per_unit)}</TableCell>
                    <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{fmtNum(it.discount_per_unit)}</TableCell>
                    <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{fmtNum(it.discount)}</TableCell>
                    <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px", fontWeight: 600, color: it.refund ? "#C62828" : "#2E7D32" }}>
                      {fmtNum(it.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* line pagination */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "8px",
              mt: "8px",
            }}
          >
            <Typography sx={{ fontSize: "11.5px", color: "#777" }}>
              {filteredLines.length === 0
                ? "No lines"
                : `Showing ${sliceStart + 1}–${Math.min(sliceStart + linesPerPage, filteredLines.length)} of ${filteredLines.length}`}
            </Typography>

            {/* always rendered — disabled on a single page, so the control never
                "disappears" and users can see where they are */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Button
                onClick={() => setLinePage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                size="small"
                startIcon={<ArrowBackIcon sx={{ fontSize: "13px" }} />}
                sx={{ fontSize: "11.5px", textTransform: "none", color: safePage === 1 ? "#9e9e9e" : "#007A99", minWidth: 0, "&:disabled": { color: "#c4c4c4" } }}
              >
                Prev
              </Button>
              <Typography sx={{ fontSize: "11.5px", color: "#3A3A3A", fontWeight: 600 }}>
                Page {safePage} of {lineTotalPages}
              </Typography>
              <Button
                onClick={() => setLinePage((p) => Math.min(lineTotalPages, p + 1))}
                disabled={safePage >= lineTotalPages}
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: "13px" }} />}
                sx={{ fontSize: "11.5px", textTransform: "none", color: safePage >= lineTotalPages ? "#9e9e9e" : "#007A99", minWidth: 0, "&:disabled": { color: "#c4c4c4" } }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

// ─── Stock Detail Dialog ────────────────────────────────────────────────────
const StockDetailDialog = ({ open, onClose, row, stockMap }) => {
  const [tab, setTab] = useState("stock");
  const [salesItems, setSalesItems] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);

  const medName = (row?.medicine_name || row?.lab_inventory_name || "")
    .toLowerCase()
    .trim();
  // stockMap is already keyed by this exact string — direct lookup, no scan
  const matched = (stockMap && medName ? stockMap[medName] : null) || null;

  const itemableId = row?.medicine_id ?? row?.itemable_id ?? matched?.id ?? null;
  const batchNo = row?.batch || "";

  // reset back to the stock tab whenever a different row is opened
  useEffect(() => {
    setTab("stock");
    setSalesItems([]);
    setSalesError(null);
  }, [row?.id]);

  // lazy-load sales only when the toggle is flipped
  useEffect(() => {
    if (!open || tab !== "sales") return;

    if (!itemableId) {
      setSalesError("No medicine id on this row — cannot load sales.");
      return;
    }

    let cancelled = false;
    setSalesLoading(true);
    setSalesError(null);

    getInvoiceItemsByBatch(itemableId, batchNo)
      .then((res) => {
        if (cancelled) return;
        setSalesItems(res?.payload?.invoice_items || []);
        setSalesLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setSalesError(e.message || "Could not load sales data");
        setSalesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, tab, itemableId, batchNo]);

  if (!row) return null;

  // A batch number alone is NOT unique — the same batch_no can exist twice with
  // different expiry dates. Identity here is batch_no + expiry_date.
  const rowBatch = (row.batch || "").toLowerCase().trim();
  const rowExpiry = normDate(row.expiry_date);

  const sameNumberBatches =
    matched?.batches?.filter(
      (b) => (b.batch_no || "").toLowerCase().trim() === rowBatch
    ) || [];

  const matchedBatch =
    // exact hit: same number AND same expiry
    sameNumberBatches.find((b) => normDate(b.expiry_date) === rowExpiry) ||
    // only one batch carries this number, so there is nothing to disambiguate
    (sameNumberBatches.length === 1 ? sameNumberBatches[0] : undefined);

  // Calculations
  const qty = Number(row.quantity) || 0;
  const mrp = Number(row.mrp) || 0;

  const stockAtPurchase = qty; // qty purchased in this order line
  const currentStockLeft =
    matchedBatch != null
      ? matchedBatch.stock_left
      : matched != null
        ? matched.total_stock_left
        : null;

  // ── Extract numeric pack size from packing string e.g. "1X10 STRIP" → 10 ──
  const packStr = matchedBatch?.packing || row.pack || "";
  const packUnits = parseInt(packStr.match(/[xX×](\d+)/)?.[1] || "1", 10) || 1;

  const freeQty = Number(row.free_quantity) || 0;
  const billAmount = Number(row.amount) || 0;

  // Value by Cost = stock_left × cost_per_unit
  // cost_per_unit = bill_amount ÷ ((qty + free) × pack_units)
  const totalUnitsLoaded = (qty + freeQty) * packUnits;
  const costPerUnit = totalUnitsLoaded > 0 ? billAmount / totalUnitsLoaded : 0;
  const valueByCost = (currentStockLeft ?? 0) * costPerUnit;

  // Value by Sale = stock_left × (MRP_per_unit × (1 - medicine_discount%))
  // Uses medicine.discount (selling discount %) — NOT the purchase line discount
  const medDiscountPct = Number(matched?.discount) || 0; // e.g. 20 = 20%
  const mrpPerUnit = packUnits > 0 ? mrp / packUnits : mrp;
  const valueBySale = (currentStockLeft ?? 0) * mrpPerUnit * (1 - medDiscountPct / 100);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #007A99 0%, #005f7a 100%)",
          color: "#fff",
          px: "24px",
          py: "14px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <InventoryIcon sx={{ fontSize: "20px" }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
            Medicine Stock Detail
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.78)", mt: "2px" }}>
            {row.medicine_name || row.lab_inventory_name || "—"}
            {row.batch ? `  ·  Batch: ${row.batch}` : ""}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <CloseIcon sx={{ fontSize: "18px" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: "24px", py: "20px", background: "#FAFCFD" }}>
        {/* Stock ⇄ Sales toggle */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: "6px" }}>
          <ToggleButtonGroup
            value={tab}
            exclusive
            size="small"
            onChange={(e, v) => v && setTab(v)}
            sx={{
              background: "#fff",
              borderRadius: "8px",
              "& .MuiToggleButton-root": {
                fontSize: "11.5px",
                textTransform: "none",
                px: "16px",
                py: "4px",
                color: "#007A99",
                borderColor: "#007A99",
                "&.Mui-selected": {
                  background: "#007A99",
                  color: "#fff",
                  "&:hover": { background: "#006c88" },
                },
              },
            }}
          >
            <ToggleButton value="stock">Stock</ToggleButton>
            <ToggleButton value="sales">Sales &amp; Refunds</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {tab === "sales" ? (
          <SalesRefundPanel
            loading={salesLoading}
            error={salesError}
            items={salesItems}
            currentStockLeft={currentStockLeft}
            batchLabel={row.batch}
          />
        ) : !matched ? (
          <Box sx={{ textAlign: "center", py: "30px", color: "#999", fontSize: "13px" }}>
            No stock record found for this medicine.
          </Box>
        ) : (
          <>
            {/* Meta chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: "16px", mt: "16px" }}>
              {matched.form_type && (
                <Chip label={matched.form_type} size="small" sx={{ fontSize: "11px", bgcolor: "#E3F2FD", color: "#1565C0" }} />
              )}
              {matched.company && (
                <Chip label={matched.company} size="small" sx={{ fontSize: "11px", bgcolor: "#F3E5F5", color: "#6A1B9A" }} />
              )}
              {/* medicine-level expiry intentionally not shown here — it is not
                  this batch's expiry; per-batch expiry is in the table below */}
            </Box>

            {/* Stat Cards — top 3 */}
            <Box sx={{ display: "flex", gap: "12px", mb: "10px" }}>
              <StatCard
                label="Stock At Purchase"
                value={stockAtPurchase}
                color="#007A99"
                sub="Units purchased in this order"
              />
              <StatCard
                label="Current Stock Left"
                value={currentStockLeft != null ? currentStockLeft : "—"}
                color={currentStockLeft != null && currentStockLeft < 20 ? "#D32F2F" : "#2E7D32"}
                sub={
                  [
                    matchedBatch
                      ? `Batch: ${row.batch}`
                      : sameNumberBatches.length > 1
                        ? "Multiple batches share this number — all combined"
                        : "All batches combined",
                    matchedBatch ? `Exp ${showDate(matchedBatch.expiry_date)}` : null,
                    matchedBatch?.packing || row.pack || null,
                  ]
                    .filter(Boolean)
                    .join("  ·  ")
                }
              />
              <StatCard
                label="Value by Cost"
                value={`₹${fmtNum(valueByCost)}`}
                color="#6A1B9A"
                sub={`${currentStockLeft ?? 0} units × ₹${fmtNum(costPerUnit)}/unit (bill ÷ ${totalUnitsLoaded} units loaded)`}
              />
            </Box>

            {/* Stat Cards — bottom row */}
            <Box sx={{ display: "flex", gap: "12px", mb: "20px" }}>
              <StatCard
                label="Value by Sale"
                value={`₹${fmtNum(valueBySale)}`}
                color="#E65100"
                sub={`${currentStockLeft ?? 0} units × ₹${fmtNum(mrpPerUnit)} MRP/unit × ${100 - medDiscountPct}% (${medDiscountPct}% selling disc)`}
              />
              <StatCard
                label="Total Stock Left"
                value={matched.total_stock_left ?? "—"}
                color="#455A64"
                sub="All batches combined"
              />
            </Box>

            <Divider sx={{ mb: "16px" }} />

            {/* Batch breakdown table */}
            {matched.batches && matched.batches.length > 0 && (
              <>
                <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#3A3A3A", mb: "10px" }}>
                  All Batch Breakdown
                </Typography>
                <TableContainer sx={{ borderRadius: "8px", border: "1px solid #E0E0E0", background: "#fff" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: "#F5F5F5" }}>
                        {["Batch No", "Expiry", "Stock Left", "MRP (₹)", "Cost (₹)", "Packing", "GST"].map((h) => (
                          <TableCell key={h} sx={{ fontSize: "11px", fontWeight: 700, color: "#555", whiteSpace: "nowrap", py: "7px", px: "10px" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matched.batches.map((b) => {
                        // identity, not batch_no — duplicates would otherwise all light up
                        const isCurrent = matchedBatch ? b === matchedBatch : false;
                        return (
                          <TableRow
                            key={b.id}
                            sx={{
                              background: isCurrent ? "#E3F7FB" : "transparent",
                              "&:hover": { background: isCurrent ? "#C9EFF7" : "#F9F9F9" },
                            }}
                          >
                            <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px", fontWeight: isCurrent ? 700 : 400 }}>
                              {b.batch_no || "—"}
                              {isCurrent && (
                                <Chip label="This" size="small" sx={{ ml: "6px", fontSize: "10px", height: "16px", bgcolor: "#007A99", color: "#fff" }} />
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{showDate(b.expiry_date)}</TableCell>
                            <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px", fontWeight: 600, color: b.stock_left < 20 ? "#D32F2F" : "#2E7D32" }}>
                              {b.stock_left ?? "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{b.amount != null ? `₹${b.amount}` : "—"}</TableCell>
                            <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{b.purchase_cost != null ? `₹${b.purchase_cost}` : "—"}</TableCell>
                            <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{b.packing || "—"}</TableCell>
                            <TableCell sx={{ fontSize: "12px", py: "6px", px: "10px" }}>{b.gst ? `${b.gst}%` : "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
// Item-level purchases: which medicines were purchased on a day / date range.
const PurchaseItemsView = ({ onSwitchToSuppliers }) => {

  // Filters state: Default Current Month (1st date to End date of current month)
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-indexed

  const firstDayStr = `${year}-${String(month + 1).padStart(2, "0")}-01`;
  const lastDayObj = new Date(year, month + 1, 0);
  const lastDayStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDayObj.getDate()).padStart(2, "0")}`;
  const [startDate, setStartDate] = useState(firstDayStr);
  const [endDate, setEndDate] = useState(lastDayStr);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Stock report state — fetched once on mount
  const [stockMap, setStockMap] = useState(null); // { [name_lower]: medicineObj }
  const [stockLoading, setStockLoading] = useState(true);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const debouncedSearch = useDebounce(searchInput, 800);

  const { data, isLoading, isFetching } = UseFetchPurchaseItems(
    page,
    pageSize,
    debouncedSearch || null,
    startDate,
    endDate,
  );

  const rows = data?.payload?.purchase_items || [];
  const totals = data?.meta?.totals || {};
  const totalCount = data?.meta?.total_count || 0;
  const totalPage = Math.max(1, Math.ceil(totalCount / pageSize));

  // ── Fetch stock report once on mount ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setStockLoading(true);
    getMedicineStockReport().then((res) => {
      if (cancelled) return;
      const medicines = res?.medicines || res?.payload?.medicines || [];
      const map = {};
      medicines.forEach((m) => {
        const key = (m.name || "").toLowerCase().trim();
        if (key) map[key] = m;
      });
      setStockMap(map);
      setStockLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const handleCheckStock = (row) => {
    setSelectedRow(row);
    setDialogOpen(true);
  };

  const setRange = (from, to) => {
    setStartDate(isoDate(from));
    setEndDate(isoDate(to));
    setPage(1);
  };

  const presets = [
    { label: "Today", apply: () => setRange(today, today) },
    {
      label: "Yesterday",
      apply: () => {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        setRange(y, y);
      },
    },
    {
      label: "Last 7 days",
      apply: () => {
        const from = new Date(today);
        from.setDate(from.getDate() - 6);
        setRange(from, today);
      },
    },
  ];

  const gstOf = (r) => {
    const c = Number(r.cgst) || 0;
    const s = Number(r.sgst) || 0;
    return c + s ? `${c + s}%` : "—";
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* controls */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            alignItems: "center",
            px: "25px",
            pb: "10px",
          }}
        >
          <TextField
            size="small"
            type="date"
            label="From"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: "150px" }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: "150px" }}
          />
          {presets.map((p) => (
            <Button
              key={p.label}
              onClick={p.apply}
              size="small"
              sx={{
                fontSize: "12px",
                textTransform: "none",
                color: "#007A99",
                border: "1px solid #007A99",
                borderRadius: "8px",
                px: "10px",
              }}
            >
              {p.label}
            </Button>
          ))}
          <TextField
            size="small"
            placeholder="Search medicine / batch"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ fontSize: "18px" }} />
                </InputAdornment>
              ),
            }}
            sx={{ ml: "auto", width: "230px" }}
          />
        </Box>

        {/* totals strip */}
        <Box
          sx={{
            display: "flex",
            gap: "18px",
            px: "25px",
            pb: "8px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {[
            ["Items", totals.total_items ?? totalCount],
            ["Quantity", totals.total_quantity ?? "—"],
            ["Free", totals.total_free_quantity ?? "—"],
            ["Amount", totals.total_amount != null ? `₹${totals.total_amount}` : "—"],
          ].map(([label, value]) => (
            <Typography key={label} sx={{ fontSize: "12.5px", color: "#3A3A3A" }}>
              {label}: <b>{value}</b>
            </Typography>
          ))}
          {isFetching && (
            <Typography sx={{ fontSize: "12.5px", color: "#9e9e9e" }}>
              updating…
            </Typography>
          )}
          {stockLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", ml: "auto" }}>
              <CircularProgress size={11} sx={{ color: "#007A99" }} />
              <Typography sx={{ fontSize: "11.5px", color: "#9e9e9e" }}>Loading stock data…</Typography>
            </Box>
          )}
        </Box>

        {/* table */}
        <TableContainer sx={{ flex: 1, px: "15px" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {[
                  "Date", "Medicine / Item", "Batch", "Expiry", "Qty", "Free",
                  "Pack", "Rate", "MRP", "Disc", "GST", "Amount", "Supplier",
                  "Bill No", "Stock Detail",
                ].map((h) => (
                  <TableCell key={h} sx={headCell}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={16} sx={{ padding: "6px 10px" }}>
                      <Skeleton height={28} />
                    </TableCell>
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={16} sx={{ ...bodyCell, textAlign: "center", py: "30px" }}>
                    No medicines purchased in this period.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} sx={{ "&:nth-of-type(odd)": { backgroundColor: "#F6FAFB" } }}>
                    <TableCell sx={bodyCell}>{showDate(r.purchase_date)}</TableCell>
                    <TableCell sx={{ ...bodyCell, whiteSpace: "normal", minWidth: "160px" }}>
                      {r.medicine_name || r.lab_inventory_name || "—"}
                    </TableCell>
                    <TableCell sx={bodyCell}>{r.batch || "—"}</TableCell>
                    <TableCell sx={bodyCell}>{showDate(r.expiry_date)}</TableCell>
                    <TableCell sx={bodyCell}>{r.quantity ?? "—"}</TableCell>
                    <TableCell sx={bodyCell}>{r.free_quantity ?? 0}</TableCell>
                    <TableCell sx={bodyCell}>{r.pack || "—"}</TableCell>
                    <TableCell sx={bodyCell}>{r.rate ?? "—"}</TableCell>
                    <TableCell sx={bodyCell}>{r.mrp ?? "—"}</TableCell>
                    <TableCell sx={bodyCell}>{r.discount || "—"}</TableCell>
                    <TableCell sx={bodyCell}>{gstOf(r)}</TableCell>
                    <TableCell sx={bodyCell}>{r.amount ?? "—"}</TableCell>
                    <TableCell sx={{ ...bodyCell, whiteSpace: "normal", minWidth: "120px" }}>
                      {r.supplier_name || "—"}
                    </TableCell>
                    <TableCell sx={bodyCell}>{r.bill_number || "—"}</TableCell>
                    {/* <TableCell sx={bodyCell}>{r.created_at || "—"}</TableCell> */}
                    {/* Check Stock button — replaces Updated At */}
                    <TableCell sx={{ ...bodyCell, padding: "4px 8px" }}>
                      <Button
                        onClick={() => handleCheckStock(r)}
                        disabled={stockLoading}
                        size="small"
                        variant="outlined"
                        startIcon={<InventoryIcon sx={{ fontSize: "12px !important" }} />}
                        sx={{
                          fontSize: "11px",
                          textTransform: "none",
                          color: "#007A99",
                          borderColor: "#007A99",
                          borderRadius: "6px",
                          px: "8px",
                          py: "3px",
                          whiteSpace: "nowrap",
                          "&:hover": { background: "#007A9912", borderColor: "#007A99" },
                        }}
                      >
                        Check Stock
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* footer: back + pagination */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "25px",
            py: "8px",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button
            onClick={onSwitchToSuppliers}
            variant="outlined"
            size="small"
            sx={{
              fontSize: "12px",
              textTransform: "none",
              color: "#007A99",
              borderColor: "#007A99",
              borderRadius: "8px",
            }}
          >
            Back to suppliers view
          </Button>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              startIcon={<ArrowBackIcon sx={{ fontSize: "14px" }} />}
              sx={{ fontSize: "12px", textTransform: "none", color: page === 1 ? "#9e9e9e" : "#007A99" }}
            >
              Prev
            </Button>
            <Typography sx={{ fontSize: "12.5px", color: "#3A3A3A" }}>
              Page {page} of {totalPage}
            </Typography>
            <Button
              onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
              disabled={page >= totalPage}
              endIcon={<ArrowForwardIcon sx={{ fontSize: "14px" }} />}
              sx={{ fontSize: "12px", textTransform: "none", color: page >= totalPage ? "#9e9e9e" : "#007A99" }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Stock Detail Dialog */}
      <StockDetailDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setSelectedRow(null); }}
        row={selectedRow}
        stockMap={stockMap}
      />
    </>
  );
};

export default PurchaseItemsView;
